import { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Landmark, Users, MapPin, Gauge, Blocks, Package, Briefcase, Megaphone, Banknote, BarChart3 } from 'lucide-react';
import { solicitudRolService, notificacionBalanceService } from '../services';
import { TopBar } from './TopBar';
import { FranjaRegularizacion } from './comprobantes/FranjaRegularizacion';
import { isAdminGlobal } from '../utils/roleHierarchy';
import { ambitoRecepcionBalance, esMenuBalanceVisible } from '../utils/permissions';

interface AdminLayoutProps {
  children: React.ReactNode;
}

export default function AdminLayout({ children }: AdminLayoutProps) {
  const location = useLocation();
  const { usuario } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [pendingRequestsCount, setPendingRequestsCount] = useState(0);
  const [pendingBalanceCount, setPendingBalanceCount] = useState(0);
  const topbarRef = useRef<HTMLElement>(null);
  // El encabezado cambia de alto: la franja de instalación agrega una fila, y en
  // pantallas angostas su texto envuelve. Medirlo evita que el menú lateral quede
  // descuadrado, que es lo que pasaba con el alto fijo de 64px.
  const [topbarHeight, setTopbarHeight] = useState(64);

  // Contadores de los badges del menú: se refrescan cada 30 s.
  useEffect(() => {
    const ambitoBalance = ambitoRecepcionBalance(usuario);
    const refrescarContadores = () => {
      solicitudRolService.getCountPendientes()
        .then(setPendingRequestsCount)
        .catch((error: unknown) => { console.error('Error loading pending requests count:', error); });
      if (ambitoBalance) {
        notificacionBalanceService.contarPendientes(ambitoBalance)
          .then(setPendingBalanceCount)
          .catch((error: unknown) => { console.error('Error loading pending balance count:', error); });
      }
    };
    refrescarContadores();
    const interval = setInterval(refrescarContadores, 30000);
    return () => clearInterval(interval);
  }, [usuario]);


  const isUserAdminGlobal = usuario?.rol?.nombre ? isAdminGlobal(usuario.rol.nombre) : false;
  const isUserAdminDiocesano = usuario?.rol?.nombre === 'admin_diocesano';
  const isUserAdminNacional = usuario?.rol?.nombre === 'admin_nacional';

  const menuItems = [
    { path: '/admin', icon: Gauge, label: 'Indicadores', badge: 0 },
    { path: '/admin/usuarios', icon: Users, label: 'Usuarios', badge: pendingRequestsCount },
    { path: '/admin/actividades', icon: Briefcase, label: 'Actividades', badge: 0 },
    { path: '/admin/campanas', icon: Megaphone, label: 'Campañas', badge: 0 },
    ...((isUserAdminDiocesano || isUserAdminNacional) ? [
      { path: '/admin/cuota-iglesia', icon: Banknote, label: 'Cuota Iglesia', badge: 0 },
    ] : []),
    ...(esMenuBalanceVisible(usuario) ? [
      { path: '/admin/balance', icon: BarChart3, label: 'Balance', badge: pendingBalanceCount },
    ] : []),
    { path: '/admin/geografia', icon: MapPin, label: 'Geografía', badge: 0 },
    ...(isUserAdminGlobal ? [
      { path: '/admin/versiones', icon: Package, label: 'Versiones', badge: 0 },
      { path: '/admin/demos', icon: Blocks, label: 'Demos', badge: 0 },
    ] : []),
  ];

  const isActive = (path: string) => {
    if (path === '/admin') {
      return location.pathname === '/admin';
    }
    return location.pathname.startsWith(path);
  };

  useLayoutEffect(() => {
    const el = topbarRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => setTopbarHeight(el.offsetHeight));
    observer.observe(el);
    setTopbarHeight(el.offsetHeight);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <TopBar
        ref={topbarRef}
        contexto={{ etiqueta: 'Administración', icon: <Landmark className="w-4 h-4" /> }}
        onMenuClick={() => setSidebarOpen(!sidebarOpen)}
        showMenuButton={true}
      />

      <div className="flex">
        <aside
          className={`
            fixed lg:sticky left-0
            w-64 bg-gradient-to-b from-purple-700 to-purple-900 text-white transition-transform duration-300 shadow-xl z-30
            ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          `}
          style={{ top: topbarHeight, height: `calc(100vh - ${topbarHeight}px)` }}
        >
          <nav className="p-4 space-y-1">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              const hasBadge = item.badge > 0;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`
                    w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200
                    ${active
                      ? 'bg-purple-600 text-white shadow-md'
                      : 'text-white hover:bg-purple-600/50'
                    }
                  `}
                >
                  <div className="relative">
                    <Icon className="w-5 h-5" />
                    {hasBadge && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center font-bold">
                        {item.badge > 9 ? '9+' : item.badge}
                      </span>
                    )}
                  </div>
                  <span className="font-medium">{item.label}</span>
                </Link>
              );
            })}
            
          </nav>
        </aside>

        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-20 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        <main className="flex-1 p-4 lg:p-6">
          <div className="max-w-7xl mx-auto">
            <FranjaRegularizacion />
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
